import {
  ImpersonationDraft,
  useAllWorkspaces,
  useImpersonationDraft,
  useWorkspaceUsers,
} from '@gripp/shared-logic';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, Platform, Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { SearchInput } from '../crop-circle/Components/SearchInput';
import { NumberTag } from '../crop-circle/Components/Tag/NumberTag';
import { Icon } from '../crop-circle/icons';
import {
  borderRadius,
  colors as CropCircleColors,
  spacing,
  typography,
} from '../crop-circle/tokens';

type PickerRow = { id: string; name: string };

const UNASSIGNED_VALUE = '';
// Sentinel for the "continue without a user" row in user mode. Picking it keeps
// userId undefined so apply() takes the workspace-only (Gripp Admin) branch.
const NO_USER_VALUE = '__no_user__';

type Mode = 'workspace' | 'user';

type ImpersonationPickerProps = {
  mode: Mode;
  // Receives the draft including the selection just made — the hook's `draft`
  // value doesn't reflect a same-tick setDraft, so callers that act on the
  // selection (e.g. user mode auto-applying the impersonation) need it passed.
  onSave: (draft: ImpersonationDraft) => void;
  onOpenFilters?: () => void;
};

export const ImpersonationPicker = ({
  mode,
  onSave,
  onOpenFilters,
}: ImpersonationPickerProps) => {
  const { t } = useTranslation();
  const { draft, setDraft } = useImpersonationDraft();

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  const allWorkspaces = useAllWorkspaces();
  const workspaceUsers = useWorkspaceUsers(
    mode === 'user' ? draft.workspaceId : undefined
  );

  const filterCount =
    (draft.subscriptionTiers?.length ?? 0) + (draft.types?.length ?? 0);

  const filteredItems = useMemo(() => {
    const q = debounced.trim().toLowerCase();

    if (mode === 'workspace') {
      return allWorkspaces.filter((w) => {
        if (q && !(w.name?.toLowerCase().includes(q) ?? false)) return false;
        if (
          draft.subscriptionTiers &&
          draft.subscriptionTiers.length > 0 &&
          !draft.subscriptionTiers.includes(w.subscriptionTierId ?? '')
        )
          return false;
        if (
          draft.types &&
          draft.types.length > 0 &&
          !draft.types.includes(w.type ?? '')
        )
          return false;
        return true;
      });
    }

    if (q) {
      return workspaceUsers.filter((u) => u.name.toLowerCase().includes(q));
    }
    return workspaceUsers;
  }, [
    mode,
    allWorkspaces,
    workspaceUsers,
    debounced,
    draft.subscriptionTiers,
    draft.types,
  ]);

  const onItemSelected = (value: string) => {
    let patch: Partial<ImpersonationDraft>;
    if (mode === 'workspace') {
      if (value === UNASSIGNED_VALUE) {
        patch = {
          workspaceId: '',
          workspaceName: t('impersonation.unassigned'),
          workspaceType: undefined,
          userId: undefined,
          userName: undefined,
        };
      } else {
        const ws = allWorkspaces.find((w) => w.id === value);
        patch = {
          workspaceId: ws?.id,
          workspaceName: ws?.name,
          workspaceType: ws?.type,
          userId: undefined,
          userName: undefined,
        };
      }
    } else if (value === NO_USER_VALUE) {
      patch = { userId: undefined, userName: undefined };
    } else {
      const user = workspaceUsers.find((u) => u.id === value);
      if (!user) return;
      patch = { userId: user.id, userName: user.name };
    }
    setDraft(patch);
    onSave({ ...draft, ...patch });
  };

  const rows: PickerRow[] = useMemo(() => {
    if (mode === 'workspace') {
      return [
        { id: UNASSIGNED_VALUE, name: t('impersonation.unassigned') },
        ...(filteredItems as PickerRow[]),
      ];
    }
    // Once a workspace is chosen, offer a "continue without a user" row so the
    // workspace-only (Gripp Admin) path is reachable from the user picker.
    if (draft.workspaceId !== undefined) {
      return [
        { id: NO_USER_VALUE, name: t('impersonation.picker.noUser') },
        ...(filteredItems as PickerRow[]),
      ];
    }
    return filteredItems as PickerRow[];
  }, [mode, filteredItems, draft.workspaceId, t]);

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <View style={styles.searchRow}>
          <View style={styles.searchInputWrapper}>
            <SearchInput
              value={search}
              onChangeText={setSearch}
              onDebounce={setDebounced}
              autoFocus={true}
              placeholder={
                mode === 'workspace'
                  ? t('impersonation.picker.searchWorkspaces')
                  : t('impersonation.picker.searchUsers')
              }
              height={40}
            />
          </View>
          {mode === 'workspace' && onOpenFilters && (
            <Pressable
              onPress={onOpenFilters}
              style={styles.filterButton}
              accessibilityRole="button"
              accessibilityLabel={t('impersonation.picker.openFilters')}
            >
              <Icon name="Filter" size={24} color={Colors.role.textStrong} />
              {filterCount > 0 && (
                <View style={styles.badgeContainer}>
                  <NumberTag number={filterCount} variant="black" />
                </View>
              )}
            </Pressable>
          )}
        </View>
      </View>

      <View style={styles.listContainer}>
        <FlatList
          style={styles.scrollViewContainer}
          // First tap selects the item even while the search keyboard is open
          // (without this the first tap only dismisses the keyboard).
          keyboardShouldPersistTaps="handled"
          data={rows}
          keyExtractor={(item, idx) => item.id || `unassigned-${idx}`}
          renderItem={({ item }) => (
            <Item
              value={item.id}
              label={item.name}
              onItemSelected={onItemSelected}
            />
          )}
          ListEmptyComponent={
            <Text style={styles.emptyText}>
              {mode === 'user' && draft.workspaceId === undefined
                ? t('impersonation.picker.userEmptyNoWorkspace')
                : t('impersonation.picker.noResults')}
            </Text>
          }
        />
      </View>
    </View>
  );
};

type ItemProps = {
  value: string;
  label: ReactNode;
  onItemSelected: (value: string) => void;
};

const Item = ({ value, label, onItemSelected }: ItemProps) => {
  return (
    <Pressable
      style={styles.itemContainer}
      onPress={() => onItemSelected(value)}
    >
      <View style={styles.itemLabelsContainer}>
        <Text numberOfLines={1} style={styles.itemLabel}>
          {label}
        </Text>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
    backgroundColor: Colors.role.canvas,
  },
  headerContainer: {
    backgroundColor: Colors.role.canvas,
    padding: spacing.lg,
    gap: spacing.lg,
    zIndex: 1,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  searchInputWrapper: {
    flex: 1,
  },
  filterButton: {
    padding: Platform.OS === 'web' ? spacing.sm : spacing.xs,
    position: 'relative',
  },
  badgeContainer: {
    position: 'absolute',
    top: -spacing.xxs,
    right: -spacing.xxs,
  },
  listContainer: {
    flex: 1,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.sm,
  },
  emptyText: {
    ...typography.body.small,
    color: Colors.role.textSecondary,
    paddingVertical: spacing['2xl'],
    textAlign: 'center',
  },
  scrollViewContainer: {
    flex: 1,
    paddingBottom: spacing['2xl'],
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    backgroundColor: Colors.role.surfaceHover,
    marginBottom: spacing.xs,
    borderRadius: borderRadius.sm,
  },
  itemLabelsContainer: {
    flex: 1,
    paddingVertical: spacing.sm,
  },
  itemLabel: {
    ...typography.body.small,
    color: Colors.role.textStrong,
    fontWeight: '400',
  },
});
